"use client";

import * as React from "react";
import { getI18n } from "react-i18next";
import type { i18n as I18nInstance } from "i18next";

import enUS from "./messages/en-US.json";
import esAR from "./messages/es-AR.json";
import {
  DEFAULT_LOCALE,
  findSupportedLocale,
  type SupportedLocaleTag,
} from "./locales";

export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  findSupportedLocale,
  type SupportedLocale,
  type SupportedLocaleTag,
} from "./locales";

/**
 * The i18next namespace the design system owns. The package never writes
 * into a host's namespaces, and a host never has to know these key names —
 * it calls `registerDesignSystemMessages` and is done.
 */
export const DESIGN_SYSTEM_NAMESPACE = "design-system";

/** The chrome copy shipped inside the package, one bundle per language. */
export const designSystemMessages: Record<SupportedLocaleTag, typeof enUS> = {
  "en-US": enUS,
  "es-AR": esAR,
};

type MessageTree = { [segment: string]: string | MessageTree };

/** Dotted key into the shipped bundles, e.g. `carousel.previousSlide`. */
export type DesignSystemMessageKey =
  | "carousel.previousSlide"
  | "carousel.nextSlide"
  | "dialog.close"
  | "languageSelect.label"
  | "pagination.morePages"
  | "sheet.close"
  | "sidebar.toggle";

function readShippedMessage(
  tag: SupportedLocaleTag,
  key: DesignSystemMessageKey,
): string | undefined {
  let node: string | MessageTree | undefined = designSystemMessages[
    tag
  ] as MessageTree;
  for (const segment of key.split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = node[segment];
  }
  return typeof node === "string" ? node : undefined;
}

/**
 * The copy the package renders on its own: the shipped bundle for the active
 * language when there is one, and the default-locale bundle otherwise. This
 * is what keeps a host that registered no catalog — or that runs no i18next
 * at all — rendering readable text instead of raw key ids.
 */
export function shippedDesignSystemText(
  key: DesignSystemMessageKey,
  language?: string,
): string {
  const locale = findSupportedLocale(language);
  return (
    (locale && readShippedMessage(locale.tag, key)) ??
    readShippedMessage(DEFAULT_LOCALE, key) ??
    key
  );
}

/**
 * Adds both shipped bundles to a host's i18next instance under the design
 * system's namespace. Idempotent, and it never overwrites copy the host has
 * already registered — a host that wants different wording keeps it.
 */
export function registerDesignSystemMessages(i18n: I18nInstance): I18nInstance {
  for (const tag of Object.keys(designSystemMessages) as SupportedLocaleTag[]) {
    i18n.addResourceBundle(
      tag,
      DESIGN_SYSTEM_NAMESPACE,
      designSystemMessages[tag],
      /* deep */ true,
      /* overwrite */ false,
    );
  }
  return i18n;
}

function activeI18n(): I18nInstance | undefined {
  try {
    return getI18n();
  } catch {
    return undefined;
  }
}

/** The language the host has resolved, or undefined when it runs no i18next. */
export function activeLanguage(): string | undefined {
  return activeI18n()?.language;
}

/**
 * Reads one design-system message, preferring the host's catalog and falling
 * back to the copy shipped in the package. Usable outside React.
 */
export function designSystemText(key: DesignSystemMessageKey): string {
  const i18n = activeI18n();
  if (i18n && typeof i18n.t === "function") {
    const translated = i18n.t(key, {
      ns: DESIGN_SYSTEM_NAMESPACE,
      defaultValue: "",
    }) as unknown as string;
    if (translated) return translated;
  }
  return shippedDesignSystemText(key, i18n?.language);
}

/**
 * Returns a reader for the design system's own chrome copy, re-rendering the
 * calling component when the host switches language. Components use this
 * instead of `useTranslation` so that they stay correct in a host with no
 * i18next instance, where `useTranslation` would render key ids.
 */
export function useDesignSystemText(): (key: DesignSystemMessageKey) => string {
  const i18n = activeI18n();
  const [language, setLanguage] = React.useState<string | undefined>(
    i18n?.language,
  );

  React.useEffect(() => {
    const instance = activeI18n();
    setLanguage(instance?.language);
    if (!instance || typeof instance.on !== "function") return;
    const onLanguageChanged = (next: string) => setLanguage(next);
    instance.on("languageChanged", onLanguageChanged);
    return () => {
      instance.off("languageChanged", onLanguageChanged);
    };
  }, [i18n]);

  return React.useCallback(
    (key: DesignSystemMessageKey) => designSystemText(key),
    // `language` is not read here; it is the signal that the host switched
    // language and the memoized reader must be replaced.
    [language],
  );
}
